/**
 * CustomerActivityTimeline Component
 * Displays a chronological timeline of customer interactions
 */

import {
  ArrowRight,
  Calendar,
  Wrench,
  Mail,
  CheckCircle,
  Clock,
  XCircle,
  MailOpen,
  Send,
  MessageSquare,
  Camera,
  DollarSign,
  Bell,
  UserCog,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Badge } from '../ui/badge';
interface ActivityItem {
  type: 'booking' | 'service' | 'campaign_email' | 'booking_event';
  id: string;
  date: string | Date;
  title: string;
  description: string;
  status?: string;
  metadata?: Record<string, unknown>;
  eventType?: string;
}

interface CustomerActivityTimelineProps {
  activities: ActivityItem[];
  isLoading?: boolean;
}

function getActivityIcon(type: ActivityItem['type']) {
  switch (type) {
    case 'booking':
      return Calendar;
    case 'service':
      return Wrench;
    case 'campaign_email':
      return Mail;
    case 'booking_event':
      return ArrowRight;
    default:
      return Clock;
  }
}

function getActivityColor(type: ActivityItem['type'], eventType?: string) {
  switch (type) {
    case 'booking':
      return { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' };
    case 'service':
      return { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200' };
    case 'campaign_email':
      return { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' };
    case 'booking_event': {
      switch (eventType) {
        case 'NOTE':
          return { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200' };
        case 'PHOTO':
          return { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' };
        case 'ESTIMATE':
          return { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200' };
        case 'APPROVAL':
          return { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' };
        case 'ASSIGNMENT':
          return { bg: 'bg-indigo-100', text: 'text-indigo-600', border: 'border-indigo-200' };
        case 'NOTIFICATION':
          return { bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-200' };
        default:
          return { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' };
      }
    }
    default:
      return { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200' };
  }
}

function getStatusBadge(type: ActivityItem['type'], status?: string) {
  if (type === 'booking') {
    switch (status) {
      case 'confirmed':
        return <Badge variant="default" className="gap-1"><CheckCircle className="h-3 w-3" /> Confirmada</Badge>;
      case 'pending':
        return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" /> Pendiente</Badge>;
      case 'cancelled':
        return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" /> Cancelada</Badge>;
      case 'completed':
        return <Badge variant="default" className="gap-1 bg-green-600"><CheckCircle className="h-3 w-3" /> Completada</Badge>;
      default:
        return null;
    }
  }

  if (type === 'campaign_email') {
    switch (status) {
      case 'opened':
        return <Badge variant="default" className="gap-1 bg-green-600"><MailOpen className="h-3 w-3" /> Abierto</Badge>;
      case 'sent':
        return <Badge variant="secondary" className="gap-1"><Send className="h-3 w-3" /> Enviado</Badge>;
      case 'queued':
        return <Badge variant="outline" className="gap-1"><Clock className="h-3 w-3" /> En cola</Badge>;
      default:
        return null;
    }
  }

  if (type === 'booking_event' && status) {
    return <Badge variant="outline" className="gap-1">{status}</Badge>;
  }

  return null;
}

function resolveBookingEventIcon(eventType?: string) {
  switch (eventType) {
    case 'STATUS_CHANGE':
      return ArrowRight;
    case 'NOTE':
      return MessageSquare;
    case 'PHOTO':
      return Camera;
    case 'ESTIMATE':
      return DollarSign;
    case 'APPROVAL':
      return CheckCircle;
    case 'ASSIGNMENT':
      return UserCog;
    case 'NOTIFICATION':
      return Bell;
    default:
      return ArrowRight;
  }
}

function formatDate(dateInput: string | Date): string {
  const date = new Date(dateInput);
  return date.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRelativeDate(dateInput: string | Date): string {
  const date = new Date(dateInput);
  const now = new Date();

  // Handle invalid or very old dates (before year 2000)
  if (isNaN(date.getTime()) || date.getFullYear() < 2000) {
    return 'Fecha no disponible';
  }

  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'Programado';
  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `Hace ${diffDays} dias`;
  if (diffDays < 30) return `Hace ${Math.floor(diffDays / 7)} semanas`;
  if (diffDays < 365) return `Hace ${Math.floor(diffDays / 30)} meses`;
  return `Hace ${Math.floor(diffDays / 365)} anos`;
}

export function CustomerActivityTimeline({ activities, isLoading }: CustomerActivityTimelineProps) {
  if (isLoading) {
    return <CustomerActivityTimelineSkeleton />;
  }

  if (activities.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <Clock className="mx-auto h-12 w-12 text-muted-foreground/50 mb-3" />
          <p className="text-muted-foreground">Sin actividad reciente</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <h4 className="text-sm font-medium">Actividad reciente</h4>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />

          {/* Activities */}
          <div className="space-y-4">
            {activities.map((activity) => {
              const eventType = activity.eventType || (activity.metadata?.eventType as string | undefined);
              const Icon = activity.type === 'booking_event'
                ? resolveBookingEventIcon(eventType)
                : getActivityIcon(activity.type);
              const colors = getActivityColor(activity.type, eventType);
              const statusBadge = getStatusBadge(activity.type, activity.status);

              return (
                <div key={`${activity.type}-${activity.id}`} className="relative flex gap-3 pl-1">
                  {/* Icon */}
                  <div className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${colors.bg} ${colors.border}`}>
                    <Icon className={`h-4 w-4 ${colors.text}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 pb-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{activity.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{activity.description}</p>
                      </div>
                      {statusBadge}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground" title={formatDate(activity.date)}>
                      {formatRelativeDate(activity.date)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CustomerActivityTimelineSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <Skeleton className="h-4 w-32" />
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex gap-3 pl-1">
              <Skeleton className="h-8 w-8 rounded-full shrink-0" />
              <div className="flex-1 space-y-2 pb-4">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
